<script>
    import { onMount } from "svelte";

    import {
        getBillingType,
        isObjectEmpty,
        showError,
    } from "../../Utils/utilFunctions.svelte";

    import { RuleType } from "../../Utils/utilEnums.svelte";

    import { DecimalInput, NotificationModal } from "./index.svelte";
    import {
        getSliceConfigByRuleType,
        getSubmitBtnDisable,
        resetAllDefaultBillingRules,
    } from "../../Pages/BillingRules/helpers/billingRuleHelper.svelte";
    import { apiMethods } from "../../Services/index.svelte";

    export let patientEncounter = {};
    export let closeModal = () => {};
    export let onSubmit = () => {};
    export let selectedBillingRule = {};
    export let doctorDetails = {};
    export let currentBillingRules = [];

    // it will be used to show this modal when create preset from encounter
    $: isSaveBillingRuleFromEncounter = !isObjectEmpty(patientEncounter);

    $: isNewBillingRule = !selectedBillingRule.hasOwnProperty("id");

    $: isSubmitBtnDisabled = getSubmitBtnDisable(billingRuleData).disable;

    $: submitBtnTooltip = getSubmitBtnDisable(billingRuleData).tooltip;

    let billingRuleData = {
        forcePatientPaysTotalFee: false,
        oopType: 0
    };

    let isSelectedBillingRuleIsDefaultAndOnly = false,
        isModifyingBillingRule = false,
        showDuplicateDefaultNoticeModal = false;

    let sliderConfig;

    onMount(() => {
        if(!isObjectEmpty(selectedBillingRule)){
            billingRuleData = { ...selectedBillingRule };
        }

        billingRuleData.accountId = isSaveBillingRuleFromEncounter
            ? patientEncounter.doctorId
            : doctorDetails.id;

        // init show billing rule amount slider
        initBillingRuleSlider();

        // determine if selected !newBillingRule is the ONLY default billing rule
        isSelectedBillingRuleIsDefaultAndOnly =
            !isNewBillingRule &&
            selectedBillingRule.isDefault &&
            currentBillingRules.filter(
                (br) => br.id != selectedBillingRule.id && br.isDefault,
            ).length == 0;
    });

    const initBillingRuleSlider = () => {
        billingRuleData.type && handleChangeBillingRuleType(false);
    };

    const handleModifyBillingRule = () => {
        // add missing properties (e.g. isDefault)
        if (!billingRuleData.hasOwnProperty("isDefault"))
            billingRuleData.isDefault = false;

        if (!billingRuleData.isDefault) modifyBillingRule();
        else {
            const currentBillingRuleHasDefault = currentBillingRules
                .filter((br) => br.id != selectedBillingRule.id)
                .some((br) => br.isDefault);

            if (!currentBillingRuleHasDefault) modifyBillingRule(true);
            else showDuplicateDefaultNoticeModal = true;
        }
    };

    const modifyBillingRule = async (isCurrentBillingRuleDefault = false) => {
        isModifyingBillingRule = true;

        if (!isCurrentBillingRuleDefault || !showDuplicateDefaultNoticeModal) {
            // billing rule is NOT default OR
            // billing rule is default but NO other default billing rules
            billingRuleData.isDefault = isCurrentBillingRuleDefault;

            if (isNewBillingRule) await createBillingRule();
            else await updateBillingRule();
        } else {
            await resetAllDefaultBillingRules(currentBillingRules);

            if (isNewBillingRule) await createBillingRule();
            else await updateBillingRule();
        }

        showDuplicateDefaultNoticeModal = false;
        isModifyingBillingRule = false;
    };

    const createBillingRule = async () => {
        await apiMethods.postMethod
            .addBillingRule(billingRuleData)
            .then((res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            "Failed to create billing rule, please refresh the page and try again and contact support if the issue persists.",
                    );
                else onSubmit(res);
            })
            .catch((err) => showError(err));

        closeModal();
    };

    const updateBillingRule = async () => {
        await apiMethods.putMethod
            .updateBillingRule(billingRuleData)
            .then((res) => {
                if (!res || res.error)
                    showError(
                        res.error ??
                            "Failed to update billing rule, please refresh the page and try again and contact support if the issue persists.",
                    );
                else onSubmit();
            })
            .catch((err) => showError(err));

        closeModal();
    };

    const handleChangeBillingRuleType = (isBillingRuleTypeChanged = true) => {
        sliderConfig = getSliceConfigByRuleType(billingRuleData.type);

        if (isBillingRuleTypeChanged)
            billingRuleData.amount = sliderConfig.amountSelector;
    };

    const handleSelectPrev = () => {
        if (!sliderConfig) return;

        const nextValue = billingRuleData.amount - sliderConfig.divisionDivider;

        billingRuleData.amount = Math.max(
            nextValue,
            sliderConfig.amountSelectorMin,
        );
    };

    const handleSelectNext = () => {
        if (!sliderConfig) return;

        const nextValue = billingRuleData.amount + sliderConfig.divisionDivider;

        billingRuleData.amount = Math.min(
            nextValue,
            sliderConfig.amountSelectorMax,
        );
    };

    const handleChangeOopType = (type = Number(0)) =>
        (billingRuleData.oopType = Number(type));

    const handleChangePatientPaysTotalFee = (value) =>
        (billingRuleData.forcePatientPaysTotalFee = value);
</script>

<NotificationModal
    isModalOpen
    title={`${isNewBillingRule ? "Create" : "Edit"} Billing Rule`}
    {closeModal}
    closeModalFunction={closeModal}
    isConfirming={isModifyingBillingRule}
    showConfirmBtn={false}
    btnOptions={[
        {
            text: isModifyingBillingRule ? "Submitting" : "Submit",
            loading: isModifyingBillingRule || isSubmitBtnDisabled,
            function: () => handleModifyBillingRule(),
            class: isSubmitBtnDisabled ? "tooltip tooltip-secondary" : "",
            tooltip: submitBtnTooltip,
        },
    ]}
    noContentPadding
>
    <div class="flex flex-col flex-1 gap-y-4 max-h-[600px] p-6 overflow-y-auto">
        <div class="flex gap-x-4">
            <p class="title mandatory">Name</p>
            <input
                type="text"
                class="input input-sm input-bordered flex-1"
                autocomplete="off"
                bind:value={billingRuleData.name}
            />
        </div>

        <div class="flex gap-x-4">
            <p class="title mandatory">Type Of Billing Rule</p>
            <select
                class="input input-bordered input-sm flex-1"
                bind:value={billingRuleData.type}
                on:change|preventDefault={handleChangeBillingRuleType}
            >
                <option value={0} selected>Select billing rule type</option>
                {#each RuleType.filter((rt) => rt.id >= 2) as rule}
                    <option value={rule.id}>
                        {rule.type}
                    </option>
                {/each}
            </select>
        </div>

        {#if sliderConfig}
            <div class="flex flex-1 flex-col gap-y-4">
                <div class="flex gap-x-4">
                    <p class="title">{getBillingType(billingRuleData.type)}</p>

                    <div class="flex flex-1 items-center gap-x-2">
                        <button
                            class="btn btn-secondary btn-sm text-[#fff]"
                            on:click|preventDefault={handleSelectPrev}
                        >
                            <i class="fa faIcon">&#xf104;</i>
                        </button>

                        <DecimalInput
                            inputClass="flex-1"
                            bind:value={billingRuleData.amount}
                        />

                        <button
                            class="btn btn-secondary btn-sm text-[#fff]"
                            on:click|preventDefault={handleSelectNext}
                        >
                            <i class="fa faIcon">&#xf105;</i>
                        </button>
                    </div>
                </div>

                <div class="flex gap-x-4">
                    <p class="title" />

                    <div class="flex flex-1">
                        <input
                            type="range"
                            class="range range-secondary"
                            min={Number(sliderConfig.amountSelectorMin)}
                            max={Number(sliderConfig.amountSelectorMax)}
                            step={sliderConfig.divisionDivider.toString()}
                            bind:value={billingRuleData.amount}
                        />
                    </div>
                </div>
            </div>
        {/if}

        <div class="flex gap-x-4">
            <p class="title">Default Rule</p>
            <div class="flex flex-1 items-center justify-center">
                <input
                    type="checkbox"
                    class="toggle toggle-secondary"
                    bind:checked={billingRuleData.isDefault}
                    disabled={isSelectedBillingRuleIsDefaultAndOnly}
                />
            </div>
        </div>
        <div class="flex gap-x-4">
            <p class="title mandatory">Patient Pays Total Fee</p>
            <div class="flex flex-1 items-center justify-center gap-x-4">
                <button
                    class={`btn btn-sm rounded rounded-full text-xs ${billingRuleData.forcePatientPaysTotalFee == false ? "btn-primary" : "btn-secondary"}`}
                    on:click|preventDefault={() => handleChangePatientPaysTotalFee(false)}
                >
                    No
                </button>
                <button
                    class={`btn btn-sm rounded rounded-full text-xs ${billingRuleData.forcePatientPaysTotalFee == true ? "btn-primary" : "btn-secondary"}`}
                    on:click|preventDefault={() => handleChangePatientPaysTotalFee(true)}
                >
                    Yes
                </button>
            </div>
        </div>
        <div class="flex gap-x-4">
            <p class="title mandatory">Charge Patient</p>
            <div class="flex flex-1 items-center justify-center gap-x-4">
                <button
                    class={`btn btn-sm rounded rounded-full text-xs ${billingRuleData.oopType == 0 ? "btn-primary" : "btn-secondary"}`}
                    on:click|preventDefault={() => handleChangeOopType(0)}
                >
                    Before
                </button>
                <button
                    class={`btn btn-sm rounded rounded-full text-xs ${billingRuleData.oopType == 1 ? "btn-primary" : "btn-secondary"}`}
                    on:click|preventDefault={() => handleChangeOopType(1)}
                >
                    After
                </button>
            </div>
        </div>
    </div>
</NotificationModal>

<NotificationModal
    bind:isModalOpen={showDuplicateDefaultNoticeModal}
    title="Notice"
    isReverseBtns
    cancelText="Confirm"
    closeModalFunction={() => (showDuplicateDefaultNoticeModal = false)}
    closeModal={() => modifyBillingRule(true)}
    submitText="No, keep default rule"
    confirmAction={() => modifyBillingRule(false)}
>
    You have other billing rules set as default, do you want to change default
    to this rule?
</NotificationModal>

<style>
    .mandatory::after {
        content: "*";
        color: #f0a;
    }

    .title {
        width: 150px;
    }
</style>
